import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getFirestore, doc, updateDoc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faTimes,
  faTrash,
  faUpload,
  faStore,
  faSpinner,
  faStar,
  faGripVertical,
  faMapMarkerAlt,
  faLocationCrosshairs,
  faCircleXmark
} from "@fortawesome/free-solid-svg-icons";

// UI Components
import { SkeletonVariants } from "./ui/Skeleton";
import { useToast } from "./ui/Toast";
import { useConfirm } from "./ui/ConfirmDialog";
import { ErrorMessage, useErrorHandler } from "./ui/ErrorMessage";
import { TextInput, TextArea, FormField } from "./ui/FormField";
import { UploadProgress, LoadingButton, PROGRESS_STATES } from "./ui/ProgressIndicator";
import { EmptyState } from "./ui/EmptyState";

// V2 directory taxonomy
import {
  CATEGORIES,
  BUSINESS_TYPES,
  ISLANDS,
  LISTING_TAGS,
  PASS_TIER_KEYS,
  PASS_TIER_LABELS,
  ALL_PASS_TIERS,
} from "../constants/categories";

// Geo constants for the map picker + island centroid fallback
import { ISLAND_CENTROIDS, USVI_BOUNDS } from "../constants/usviGeo";

// Validation
import { validators, useFormValidation } from "../utils/formValidation";
import { sanitizeListingPayload } from "../utils/sanitizeListingInput";
import { getThumbnailUrl } from "../utils/imageVariants";

import "../styles/EditBusinessListings.css";

// Validation schema for business listing
const validationSchema = {
  name: [
    validators.required("Business name is required"),
    validators.minLength(3, "Name must be at least 3 characters"),
    validators.maxLength(100, "Name must be less than 100 characters")
  ],
  description: [
    validators.required("Description is required"),
    validators.minLength(20, "Description should be at least 20 characters"),
    validators.maxLength(2000, "Description must be less than 2000 characters")
  ],
  street: [
    validators.required("Street address is required"),
    validators.minLength(3, "Street address looks too short"),
    validators.maxLength(200, "Street address must be less than 200 characters")
  ],
  city: [
    validators.maxLength(100, "City / town must be less than 100 characters")
  ],
  zip: [
    validators.required("Zip code is required"),
    validators.pattern(/^008\d{2}$/, "USVI zip codes are 5 digits starting with 008 (e.g. 00802)")
  ],
  contact: [
    validators.required("Contact email is required"),
    validators.email("Please enter a valid email address")
  ],
  phone: [
    validators.phone("Please enter a valid phone number")
  ],
  type: [
    validators.required("Business type is required")
  ],
  website: [
    validators.url("Website must be a valid URL (https://example.com)")
  ]
};

function EditBusinessListing() {
  const { id: listingId } = useParams();
  const { user, firebaseUser } = useAuth();
  const { isBusinessMember, loading: plansLoading } = usePlans();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { error: fetchError, setError: setFetchError, clearError } = useErrorHandler();
  
  // Check if this is a new listing
  const isNewListing = !listingId || listingId === 'new';
  
  const [loading, setLoading] = useState(!isNewListing); // Don't show loading for new listings
  const [saving, setSaving] = useState(false);
  // Unified gallery: ordered array of { url } across both existing and new uploads.
  const [gallery, setGallery] = useState([]);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]); // Track individual file progress
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  // V2 directory fields (Phase 4). Kept in their own state — the underlying
  // `useFormValidation` hook is text-only, so these structured fields are
  // hydrated from Firestore in the load effect and merged in handleSave.
  const [category, setCategory] = useState("");
  const [island, setIsland] = useState("");
  const [tags, setTags] = useState([]);
  const [eligiblePassTiers, setEligiblePassTiers] = useState(ALL_PASS_TIERS);
  const [earnsDoubloons, setEarnsDoubloons] = useState(false);
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  // Weekly operating hours — shape matches BusinessHours in firestore.ts.
  // Day keys are monday..sunday; each day is { open, close, closed }.
  const [hours, setHours] = useState(makeDefaultHours());
  // Optional precise coordinates from the map picker. null means "use island
  // centroid fallback" via resolvePlotCoords on the map page.
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  const storage = getStorage();
  const db = getFirestore();

  // Form validation hook - must be called unconditionally
  const {
    values: formData,
    isDirty,
    handleSubmit,
    resetForm,
    getFieldProps,
    getFieldError
  } = useFormValidation({
    name: "",
    description: "",
    street: "",
    street2: "",
    city: "",
    zip: "",
    contact: "",
    phone: "",
    type: "",
    website: ""
  }, validationSchema);

  useEffect(() => {
    const fetchListing = async () => {
      // If this is a new listing, don't fetch anything
      if (isNewListing) {
        setLoading(false);
        return;
      }
      
      if (!listingId) {
        setFetchError(new Error("No listing ID provided"));
        setLoading(false);
        return;
      }
      
      clearError();
      setLoading(true);
      
      try {
        const docRef = doc(db, "businessListings", listingId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Back-compat: legacy listings may only have a single `location`
          // free-text string. Hydrate the new structured fields from
          // `data.address` when present, else best-effort split the legacy
          // string into city + everything-else (street). Keeping street
          // empty when we can't be sure forces the owner to confirm.
          const addr = (data.address && typeof data.address === "object") ? data.address : {};
          let street = addr.street || "";
          let street2 = addr.street2 || "";
          let city = addr.city || "";
          let zip = addr.zip || "";
          if (!street && !city && data.location) {
            const parts = String(data.location).split(",").map((s) => s.trim()).filter(Boolean);
            if (parts.length >= 2) {
              // Heuristic: "Street, City, Island" or "City, Island". Drop the
              // trailing island (it's already a separate field).
              const islandLabels = ISLANDS.map((i) => i.label.toLowerCase());
              const last = parts[parts.length - 1].toLowerCase();
              const trimmed = islandLabels.includes(last) ? parts.slice(0, -1) : parts;
              if (trimmed.length === 1) {
                city = trimmed[0];
              } else {
                street = trimmed.slice(0, -1).join(", ");
                city = trimmed[trimmed.length - 1];
              }
            } else if (parts.length === 1) {
              city = parts[0];
            }
          }
          resetForm({
            name: data.name || "",
            description: data.description || "",
            street,
            street2,
            city,
            zip,
            contact: data.contact || "",
            phone: data.phone || "",
            type: data.type || ""
          });
          setGallery((data.images || []).map((url) => ({ url })));
          setProfileImageUrl(data.profileImageUrl || (data.images && data.images[0]) || null);
          // V2 directory fields — fall back to permissive defaults if absent.
          setCategory(data.category || "");
          setIsland(data.island || "");
          setTags(Array.isArray(data.tags) ? data.tags : []);
          setEligiblePassTiers(
            Array.isArray(data.eligiblePassTiers) && data.eligiblePassTiers.length > 0
              ? data.eligiblePassTiers
              : ALL_PASS_TIERS
          );
          setEarnsDoubloons(Boolean(data.earnsDoubloons));
          setBookingEnabled(Boolean(data.bookingEnabled));
          setBookingUrl(data.bookingUrl || "");
          setHours(normalizeHours(data.hours));
          // Coordinates (top-level or nested) — null when absent so the
          // picker falls back to the island centroid.
          const latRaw = data.latitude ?? data.coordinates?.latitude;
          const lngRaw = data.longitude ?? data.coordinates?.longitude;
          const lat = typeof latRaw === "number" ? latRaw : Number(latRaw);
          const lng = typeof lngRaw === "number" ? lngRaw : Number(lngRaw);
          setLatitude(Number.isFinite(lat) ? lat : null);
          setLongitude(Number.isFinite(lng) ? lng : null);
        } else {
          setFetchError(new Error("Listing not found"));
        }
      } catch (err) {
        console.error("Error fetching listing:", err);
        setFetchError(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchListing();
  }, [listingId, isNewListing, db, resetForm, setFetchError, clearError]);

  // Show loading while plans are being fetched (after all hooks)
  if (plansLoading) {
    return (
      <div className="edit-business-listing">
        <div className="edit-loading-state">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Any business tier can create/edit listings — the free Crew Listing included.
  if (!isBusinessMember()) {
    return (
      <div className="edit-business-listing">
        <EmptyState
          icon={faStore}
          title="Business Owner Access Required"
          description="Creating and editing business listings requires a Business Owner subscription."
          actions={[
            {
              label: 'View Subscription Options',
              onClick: () => navigate('/account/subscriptions'),
              primary: true
            },
            {
              label: 'Go Back',
              onClick: () => navigate(-1)
            }
          ]}
        />
      </div>
    );
  }

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    
    // Initialize progress tracking for each file
    const initialProgress = files.map((file, index) => ({
      id: `upload-${Date.now()}-${index}`,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      state: PROGRESS_STATES.LOADING,
      url: null
    }));
    setUploadProgress(initialProgress);

    const uploadPromises = files.map((file, index) => {
      return new Promise((resolve) => {
        // Validate file (whitelist MIME + ext, size bounds, decodability,
        // sanitize filename for storage path).
        const validation = validateImageForUpload(file);
        if (!validation.ok) {
          setUploadProgress(prev => prev.map((p, i) =>
            i === index ? { ...p, state: PROGRESS_STATES.ERROR, error: validation.reason } : p
          ));
          showToast(`${file.name}: ${validation.reason}`, "warning");
          resolve(null);
          return;
        }

        // Verify the bytes actually decode as the claimed image (defends
        // against polyglots / spoofed Content-Type). Falls back to allow
        // if createImageBitmap is unavailable.
        const verify = typeof window !== "undefined" && typeof window.createImageBitmap === "function"
          ? window.createImageBitmap(file).then(
              (bmp) => {
                const ok = bmp.width > 0 && bmp.height > 0 && bmp.width <= 8000 && bmp.height <= 8000;
                bmp.close?.();
                return ok;
              },
              () => false
            )
          : Promise.resolve(true);

        verify.then((decoded) => {
          if (!decoded) {
            setUploadProgress(prev => prev.map((p, i) =>
              i === index ? { ...p, state: PROGRESS_STATES.ERROR, error: "Invalid or corrupted image" } : p
            ));
            showToast(`${file.name} could not be decoded as an image`, "warning");
            resolve(null);
            return;
          }

          const safeName = validation.safeName;
          const storageRef = ref(
            storage,
            `businessListings/${listingId}/${Date.now()}-${safeName}`
          );

          // Use resumable upload with progress tracking. Pin the
          // contentType to the validated whitelist value so the bucket
          // doesn't store a user-supplied string.
          const uploadTask = uploadBytesResumable(storageRef, file, {
            contentType: validation.contentType,
          });

          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(prev => prev.map((p, i) =>
                i === index ? { ...p, progress } : p
              ));
            },
            (error) => {
              console.error("Upload error:", error);
              setUploadProgress(prev => prev.map((p, i) =>
                i === index ? { ...p, state: PROGRESS_STATES.ERROR, error: error.message } : p
              ));
              resolve(null);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(prev => prev.map((p, i) =>
                i === index ? { ...p, progress: 100, state: PROGRESS_STATES.SUCCESS, url: downloadURL } : p
              ));
              resolve(downloadURL);
            }
          );
        });
      });
    });

    try {
      const results = await Promise.all(uploadPromises);
      const uploadedImages = results.filter(url => url !== null);

      if (uploadedImages.length > 0) {
        setGallery((g) => [...g, ...uploadedImages.map((url) => ({ url }))]);
        // If no profile image picked yet, default to the first uploaded image.
        setProfileImageUrl((cur) => cur || uploadedImages[0] || null);
        showToast(`${uploadedImages.length} image(s) uploaded successfully!`, "success");
      }
      
      // Clear progress after a delay to show completion state
      setTimeout(() => {
        setUploadProgress([]);
      }, 2000);
    } catch (err) {
      console.error("Error uploading images:", err);
      showToast("Failed to upload images. Please try again.", "error");
    } finally {
      setUploadingImages(false);
    }
  };

  // ---- Drag-and-drop reorder (HTML5, no new deps) ----
  const handleDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set for dragstart to fire.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { /* noop */ }
  };

  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropIndex !== idx) setDropIndex(idx);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      handleDragEnd();
      return;
    }
    setGallery((g) => {
      const next = [...g];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    handleDragEnd();
  };

  const handleSetProfile = (url) => {
    setProfileImageUrl(url);
    // Hoist the chosen image to the front of the array so consumers that read
    // `images[0]` continue to work without changes.
    setGallery((g) => {
      const idx = g.findIndex((x) => x.url === url);
      if (idx <= 0) return g;
      const next = [...g];
      const [moved] = next.splice(idx, 1);
      next.unshift(moved);
      return next;
    });
  };

  const handleRemoveImage = async (index) => {
    const confirmed = await confirm({
      title: "Remove Image",
      message: "Are ye sure ye want to remove this image?",
      confirmText: "Remove",
      variant: "warning"
    });

    if (!confirmed) return;

    setGallery((g) => {
      const next = [...g];
      const [removed] = next.splice(index, 1);
      // If the removed image was the current profile image, fall back to the
      // new first image (or null).
      if (removed && removed.url === profileImageUrl) {
        setProfileImageUrl(next[0] ? next[0].url : null);
      }
      return next;
    });

    showToast("Image removed", "info");
  };

  const handleSave = async () => {
    if (!user) {
      showToast("Ye must be logged in to save changes!", "error");
      return;
    }

    setSaving(true);
    
    try {
      const orderedUrls = gallery.map((g) => g.url);
      const resolvedProfile = profileImageUrl && orderedUrls.includes(profileImageUrl)
        ? profileImageUrl
        : (orderedUrls[0] || null);
      // Persist a precomputed thumbnail URL so card grids don't have to
      // derive variants client-side every render (see src/utils/imageVariants.js).
      // Falls back to the original URL when the thumbnail trigger hasn't yet
      // produced variants (first save / pre-backfill).
      const thumbnailUrl = resolvedProfile ? getThumbnailUrl(resolvedProfile) : null;
      const islandLabel = ISLANDS.find((i) => i.key === island)?.label || "";
      const street = (formData.street || "").trim();
      const street2 = (formData.street2 || "").trim();
      const city = (formData.city || "").trim();
      const zip = (formData.zip || "").trim();
      const addressLine = [street, street2].filter(Boolean).join(", ");
      // Back-compat: keep the legacy single `location` string in sync so
      // older readers (search, list cards) continue to render an address.
      const composedLocation = [addressLine, city, islandLabel, zip]
        .filter(Boolean)
        .join(", ");
      // Strip the address sub-fields out of formData; they're persisted in the
      // structured `address` object below (and a back-compat `location` string).
      const { street: _street, street2: _street2, city: _city, zip: _zip, ...restFormData } = formData;
      void _street; void _street2; void _city; void _zip;
      const listingData = {
        ...restFormData,
        location: composedLocation,
        address: {
          street,
          street2: street2 || null,
          ...(city ? { city } : {}),
          zip,
        },
        latitude: typeof latitude === "number" && Number.isFinite(latitude) ? latitude : null,
        longitude: typeof longitude === "number" && Number.isFinite(longitude) ? longitude : null,
        images: orderedUrls,
        profileImageUrl: resolvedProfile,
        thumbnailUrl,
        // V2 directory fields
        category: category || null,
        island: island || null,
        tags: Array.isArray(tags) ? tags : [],
        eligiblePassTiers: Array.isArray(eligiblePassTiers) && eligiblePassTiers.length > 0
          ? eligiblePassTiers
          : ALL_PASS_TIERS,
        earnsDoubloons: Boolean(earnsDoubloons),
        bookingEnabled: Boolean(bookingEnabled) && Boolean((bookingUrl || "").trim()),
        bookingUrl: (bookingUrl || "").trim() || null,
        website: (formData.website || "").trim() || null,
        hours: sanitizeHoursForSave(hours),
        updatedAt: new Date().toISOString()
      };

      // Defense-in-depth: scrub every user-supplied text field (strip HTML,
      // control chars, AI prompt-injection markers, clamp length, reject
      // non-http(s) URLs). The cloud function MUST also sanitize on the
      // server before any persistence — never trust the client.
      const scrubbed = sanitizeListingPayload(listingData);
      Object.assign(listingData, scrubbed);
      if (!listingData.name || !listingData.description || !listingData.type) {
        showToast("Please fill in business name, description, and type.", "error");
        setSaving(false);
        return;
      }

      if (isNewListing) {
        // Create a new listing
        // ownerId MUST be firebaseUser.uid to match Firestore security rules
        const ownerId = firebaseUser?.uid;
        const wixMemberId = user?.member?._id || null;
        const ownerEmail = user?.member?.loginEmail || user?.member?.email || firebaseUser?.email;
        
        if (!ownerId) {
          showToast("Unable to identify user. Please ensure you're fully logged in.", "error");
          setSaving(false);
          return;
        }
        
        const newListingData = {
          ...listingData,
          ownerId: ownerId,  // Firebase UID - required for security rules
          wixMemberId: wixMemberId,  // Wix member ID for reference
          ownerEmail: ownerEmail || null,
          status: 'pending', // New listings require approval
          createdAt: serverTimestamp(),
          viewCount: 0,
          averageRating: null
        };
        
        const docRef = await addDoc(collection(db, "businessListings"), newListingData);
        showToast("Listing created successfully! It will be reviewed shortly.", "success");
        navigate(`/edit-business-listing/${docRef.id}`);
      } else {
        // Update existing listing
        const docRef = doc(db, "businessListings", listingId);
        await updateDoc(docRef, listingData);
        showToast("Listing saved successfully!", "success");
        navigate("/business-dashboard");
      }
    } catch (err) {
      console.error("Error saving listing:", err);
      showToast("Failed to save listing. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (isDirty || gallery.length > 0) {
      const confirmed = await confirm({
        title: "Discard Changes",
        message: "Ye have unsaved changes. Are ye sure ye want to leave?",
        confirmText: "Discard",
        cancelText: "Keep Editing",
        variant: "warning"
      });
      
      if (!confirmed) return;
    }
    
    navigate("/business-dashboard");
  };

  if (fetchError) {
    return (
      <div className="edit-business-listing">
        <ErrorMessage 
          error={fetchError} 
          onRetry={() => window.location.reload()}
          showHomeButton={true}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="edit-business-listing">
        <div className="edit-form-skeleton">
          <SkeletonVariants.Card />
          <SkeletonVariants.Card />
          <SkeletonVariants.Card />
        </div>
      </div>
    );
  }

  return (
    <div className="edit-business-listing">
      <div className="edit-header">
        <h1>Edit Business Listing</h1>
        {isDirty && (
          <span className="unsaved-indicator">Unsaved changes</span>
        )}
      </div>

      <form onSubmit={handleSubmit(handleSave)}>
        <section className="edit-section" aria-labelledby="section-basics">
          <header className="edit-section__header">
            <h2 id="section-basics" className="edit-section__title">Basics</h2>
            <p className="edit-section__subtitle">The essentials travelers see first.</p>
          </header>
          <div className="edit-section__body">
            <TextInput
              label="Business Name"
              name="name"
              required
              placeholder="Enter your business name"
              {...getFieldProps("name")}
              {...getFieldError("name")}
            />

            <TextArea
              label="Description"
              name="description"
              required
              rows={5}
              maxLength={2000}
              placeholder="Describe your business, services, and what makes you unique..."
              value={formData.description}
              {...getFieldProps("description")}
              {...getFieldError("description")}
            />

            <div className="edit-row edit-row--2">
              <TextInput
                label="Contact Email"
                name="contact"
                type="email"
                required
                placeholder="business@example.com"
                {...getFieldProps("contact")}
                {...getFieldError("contact")}
              />

              <TextInput
                label="Phone Number"
                name="phone"
                type="tel"
                placeholder="(340) 123-4567"
                {...getFieldProps("phone")}
                {...getFieldError("phone")}
              />
            </div>

            <TextInput
              label="Website (optional)"
              name="website"
              type="url"
              placeholder="https://your-business.example.com"
              helpText="Include the full URL starting with http:// or https://"
              {...getFieldProps("website")}
              {...getFieldError("website")}
            />
          </div>
        </section>

        <section className="edit-section" aria-labelledby="section-hours">
          <header className="edit-section__header">
            <h2 id="section-hours" className="edit-section__title">Hours</h2>
            <p className="edit-section__subtitle">When travelers can visit. Leave blank for any day you'd rather not publish.</p>
          </header>
          <div className="edit-section__body">
            <div className="hours-grid" role="group" aria-label="Weekly operating hours">
              {DAYS_OF_WEEK.map(({ key, label }) => {
                const day = hours[key] || { open: "", close: "", closed: false };
                return (
                  <div key={key} className="hours-grid__row">
                    <span className="hours-grid__day">{label}</span>
                    <label className="v2-checkbox hours-grid__closed">
                      <input
                        type="checkbox"
                        checked={day.closed}
                        onChange={(e) =>
                          setHours((prev) => ({
                            ...prev,
                            [key]: e.target.checked
                              ? { open: "", close: "", closed: true }
                              : { ...prev[key], closed: false },
                          }))
                        }
                      />
                      <span>Closed</span>
                    </label>
                    <input
                      type="time"
                      className="v2-text hours-grid__time"
                      aria-label={`${label} open time`}
                      value={day.open}
                      disabled={day.closed}
                      onChange={(e) =>
                        setHours((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], open: e.target.value, closed: false },
                        }))
                      }
                    />
                    <span className="hours-grid__sep" aria-hidden="true">–</span>
                    <input
                      type="time"
                      className="v2-text hours-grid__time"
                      aria-label={`${label} close time`}
                      value={day.close}
                      disabled={day.closed}
                      onChange={(e) =>
                        setHours((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], close: e.target.value, closed: false },
                        }))
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="edit-section" aria-labelledby="section-location">
          <header className="edit-section__header">
            <h2 id="section-location" className="edit-section__title">
              <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden="true" /> Location
            </h2>
            <p className="edit-section__subtitle">
              Used to plot your spot on the Guild map and show travelers how to reach you.
            </p>
          </header>
          <div className="edit-section__body">
            <TextInput
              label="Street Address"
              name="street"
              required
              placeholder="e.g., 5304 Long Bay Road"
              {...getFieldProps("street")}
              {...getFieldError("street")}
            />

            <TextInput
              label="Suite / Unit (optional)"
              name="street2"
              placeholder="e.g., Suite 2B, Building C, Slip 14"
              {...getFieldProps("street2")}
              {...getFieldError("street2")}
            />

            <div className="edit-row edit-row--2">
              <TextInput
                label="City / Town (optional)"
                name="city"
                placeholder="e.g., Charlotte Amalie"
                list="usvi-towns"
                helpText="Optional — many USVI addresses don't have a town. Leave blank if not applicable."
                {...getFieldProps("city")}
                {...getFieldError("city")}
              />
              <datalist id="usvi-towns">
                <option value="Charlotte Amalie" />
                <option value="Red Hook" />
                <option value="Frenchtown" />
                <option value="Havensight" />
                <option value="Cruz Bay" />
                <option value="Coral Bay" />
                <option value="Christiansted" />
                <option value="Frederiksted" />
              </datalist>

              <TextInput
                label="Zip Code"
                name="zip"
                required
                inputMode="numeric"
                placeholder="00802"
                helpText="USVI zip codes are 00801–00851."
                {...getFieldProps("zip")}
                {...getFieldError("zip")}
              />
            </div>

            <FormField label="Island" name="island" required>
              <select
                id="island"
                className="v2-select"
                value={island}
                onChange={(e) => setIsland(e.target.value)}
              >
                <option value="">— Pick an island —</option>
                {ISLANDS.map((i) => (
                  <option key={i.key} value={i.key}>{i.label}</option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Pin on the map"
              name="map-pin"
              helpText="Drag the pin to your exact location so travelers can find you. Skip this and you'll appear at the island's general area."
            >
              <LocationPicker
                latitude={latitude}
                longitude={longitude}
                island={island}
                onChange={(coords) => {
                  setLatitude(coords?.lat ?? null);
                  setLongitude(coords?.lng ?? null);
                }}
              />
            </FormField>
          </div>
        </section>

        <section className="edit-section" aria-labelledby="section-discovery">
          <header className="edit-section__header">
            <h2 id="section-discovery" className="edit-section__title">Discovery</h2>
            <p className="edit-section__subtitle">How travelers find you in directory filters and search.</p>
          </header>
          <div className="edit-section__body">
            <FormField
              label="Business Type"
              name="type"
              required
              {...getFieldError("type")}
              helpText="High-level category used by the directory & map."
            >
              <select
                id="type"
                name="type"
                className="v2-select"
                value={formData.type || ""}
                onChange={(e) =>
                  getFieldProps("type").onChange({
                    target: { name: "type", value: e.target.value },
                  })
                }
                onBlur={getFieldProps("type").onBlur}
              >
                <option value="">— Pick a type —</option>
                {BUSINESS_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Category" name="category">
              <select
                id="category"
                className="v2-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">— Pick a category —</option>
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Vibe / Tags" name="tags" helpText="Pick all that apply.">
              <div className="v2-checkbox-grid">
                {LISTING_TAGS.map((t) => (
                  <label key={t.key} className="v2-checkbox">
                    <input
                      type="checkbox"
                      checked={tags.includes(t.key)}
                      onChange={(e) =>
                        setTags((prev) =>
                          e.target.checked
                            ? [...prev, t.key]
                            : prev.filter((k) => k !== t.key)
                        )
                      }
                    />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>
            </FormField>
          </div>
        </section>

        <section className="edit-section" aria-labelledby="section-program">
          <header className="edit-section__header">
            <h2 id="section-program" className="edit-section__title">Guild Program</h2>
            <p className="edit-section__subtitle">Which travelers your offer welcomes, and how the Guild rewards visits.</p>
          </header>
          <div className="edit-section__body">
            <FormField
              label="Eligible Pass Tiers"
              name="eligiblePassTiers"
              helpText="Which traveler passes can redeem your Guild offer. Leave all checked to welcome every Guild member."
            >
              <div className="v2-checkbox-grid">
                {Object.values(PASS_TIER_KEYS).map((key) => (
                  <label key={key} className="v2-checkbox">
                    <input
                      type="checkbox"
                      checked={eligiblePassTiers.includes(key)}
                      onChange={(e) =>
                        setEligiblePassTiers((prev) =>
                          e.target.checked
                            ? [...prev, key]
                            : prev.filter((k) => k !== key)
                        )
                      }
                    />
                    <span>{PASS_TIER_LABELS[key]}</span>
                  </label>
                ))}
              </div>
            </FormField>

            <FormField label="Doubloons" name="earnsDoubloons">
              <label className="v2-checkbox">
                <input
                  type="checkbox"
                  checked={earnsDoubloons}
                  onChange={(e) => setEarnsDoubloons(e.target.checked)}
                />
                <span>Travelers earn Doubloons when they visit / redeem here</span>
              </label>
            </FormField>

            <FormField label="Bookings" name="bookings" helpText="Travelers can book through the Guild once you complete Stripe Connect onboarding from the Payments tab of your dashboard. You can also provide an external booking URL here as a fallback.">
              <label className="v2-checkbox">
                <input
                  type="checkbox"
                  checked={bookingEnabled}
                  onChange={(e) => setBookingEnabled(e.target.checked)}
                />
                <span>Enable a &ldquo;Book Now&rdquo; button on this listing</span>
              </label>
              {bookingEnabled && (
                <>
                  <input
                    type="url"
                    className="v2-text"
                    value={bookingUrl}
                    onChange={(e) => setBookingUrl(e.target.value)}
                    placeholder="https://your-booking-page.example.com (optional fallback)"
                    style={{ marginTop: "0.5rem" }}
                  />
                  <p className="help-text" style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                    Guild-hosted bookings require a connected Stripe account.{" "}
                    <Link to="/business-dashboard?tab=payments">Set up payouts &rarr;</Link>
                  </p>
                </>
              )}
            </FormField>
          </div>
        </section>

        <section className="edit-section" aria-labelledby="section-photos">
          <header className="edit-section__header">
            <h2 id="section-photos" className="edit-section__title">Photos</h2>
            <p className="edit-section__subtitle">Show travelers what to expect. The first image is your profile shot.</p>
          </header>
          <div className="edit-section__body">
            <FormField label="Business Images" name="images">
          <div className="image-upload-section">
            <label className="upload-btn" style={{ opacity: uploadingImages ? 0.6 : 1 }}>
              <FontAwesomeIcon icon={faUpload} />
              {uploadingImages ? "Uploading..." : "Upload Images"}
              <input 
                type="file" 
                multiple 
                accept="image/*"
                onChange={handleImageUpload} 
                disabled={uploadingImages}
                style={{ display: 'none' }}
              />
            </label>
            <span className="upload-hint">Max 5MB per image. JPG, PNG, or GIF.</span>
          </div>
          
          {/* Upload Progress Indicators */}
          {uploadProgress.length > 0 && (
            <div className="upload-progress-list">
              {uploadProgress.map((upload) => (
                <UploadProgress
                  key={upload.id}
                  progress={upload.progress}
                  state={upload.state}
                  fileName={upload.fileName}
                  fileSize={upload.fileSize}
                />
              ))}
            </div>
          )}
        </FormField>

        {/* Unified gallery — drag to reorder, star to set as profile image */}
        {gallery.length > 0 && (
          <div className="image-gallery image-gallery--unified">
            <h4>Images ({gallery.length})</h4>
            <p className="image-gallery__hint">
              Drag to reorder. Click the star to set the profile image.
              The first image is what travellers see in search results.
            </p>
            <div className="image-preview">
              {gallery.map((g, index) => {
                const isProfile = g.url === profileImageUrl;
                const isDragging = dragIndex === index;
                const isDropTarget = dropIndex === index && dragIndex !== null && dragIndex !== index;
                return (
                  <div
                    key={`${g.url}-${index}`}
                    className={[
                      'image-item',
                      isProfile ? 'image-item--profile' : '',
                      isDragging ? 'image-item--dragging' : '',
                      isDropTarget ? 'image-item--drop-target' : '',
                    ].filter(Boolean).join(' ')}
                    draggable
                    onDragStart={handleDragStart(index)}
                    onDragOver={handleDragOver(index)}
                    onDrop={handleDrop(index)}
                    onDragEnd={handleDragEnd}
                  >
                    <img src={g.url} alt={`Listing ${index + 1}`} />
                    {isProfile && (
                      <span className="image-profile-badge">Profile</span>
                    )}
                    {!isProfile && (
                      <button
                        type="button"
                        className="set-profile-btn"
                        onClick={() => handleSetProfile(g.url)}
                        title="Set as profile image"
                        aria-label={`Set image ${index + 1} as profile image`}
                      >
                        <FontAwesomeIcon icon={faStar} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(index)}
                      title="Remove image"
                      aria-label={`Remove image ${index + 1}`}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                    <div className="image-actions" aria-hidden="true">
                      <FontAwesomeIcon icon={faGripVertical} /> Drag to reorder
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </div>
        </section>

        <div className="form-actions">
          <LoadingButton 
            type="submit" 
            className="save-btn"
            loading={saving}
            loadingText="Saving..."
            variant="primary"
          >
            <FontAwesomeIcon icon={faSave} />
            Save Changes
          </LoadingButton>
          <button 
            type="button" 
            onClick={handleCancel} 
            className="cancel-btn"
            disabled={saving}
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Business hours helpers — keep the BusinessHours shape canonical so the
// admin "missing recommended: hours" check sees a populated object only when
// the owner actually filled something in.
// ---------------------------------------------------------------------------
const DAYS_OF_WEEK = [
  { key: "monday",    label: "Monday" },
  { key: "tuesday",   label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday",  label: "Thursday" },
  { key: "friday",    label: "Friday" },
  { key: "saturday",  label: "Saturday" },
  { key: "sunday",    label: "Sunday" },
];

function makeDefaultHours() {
  return DAYS_OF_WEEK.reduce((acc, d) => {
    acc[d.key] = { open: "", close: "", closed: false };
    return acc;
  }, {});
}

function normalizeHours(raw) {
  const base = makeDefaultHours();
  if (!raw || typeof raw !== "object") return base;
  for (const { key } of DAYS_OF_WEEK) {
    const day = raw[key];
    if (day && typeof day === "object") {
      base[key] = {
        open: typeof day.open === "string" ? day.open : "",
        close: typeof day.close === "string" ? day.close : "",
        closed: Boolean(day.closed),
      };
    }
  }
  return base;
}

// If the owner hasn't filled in any day, persist null so the admin
// "recommended: hours" badge still shows. Otherwise persist a fully shaped
// object so downstream readers don't have to defend against missing keys.
function sanitizeHoursForSave(state) {
  if (!state || typeof state !== "object") return null;
  const anyFilled = DAYS_OF_WEEK.some(({ key }) => {
    const d = state[key];
    return d && (d.closed || (d.open && d.close));
  });
  if (!anyFilled) return null;
  return DAYS_OF_WEEK.reduce((acc, { key }) => {
    const d = state[key] || {};
    if (d.closed) {
      acc[key] = { open: "", close: "", closed: true };
    } else {
      acc[key] = {
        open: typeof d.open === "string" ? d.open : "",
        close: typeof d.close === "string" ? d.close : "",
        closed: false,
      };
    }
    return acc;
  }, {});
}

// ---------------------------------------------------------------------------
// Image upload validation — whitelist MIME + extension, enforce size bounds,
// scrub the filename so the storage path stays predictable. Decodability
// check happens in the upload handler via createImageBitmap.
// ---------------------------------------------------------------------------
const IMAGE_MIME_WHITELIST = Object.freeze({
  "image/jpeg": "jpg",
  "image/png":  "png",
  "image/webp": "webp",
  "image/gif":  "gif",
});
const IMAGE_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const IMAGE_MIN_BYTES = 256;             // reject 0-byte / truncated files

function sanitizeUploadFileName(name, fallbackExt) {
  const raw = String(name || "upload").trim();
  // Strip path separators + anything outside safe ASCII charset.
  const base = raw.replace(/[/\\]/g, "_")
                  .replace(/[^a-zA-Z0-9._-]/g, "_")
                  .replace(/_+/g, "_");
  const dot = base.lastIndexOf(".");
  let stem = dot > 0 ? base.slice(0, dot) : base;
  let ext  = dot > 0 ? base.slice(dot + 1).toLowerCase() : fallbackExt;
  if (!stem) stem = "upload";
  if (stem.length > 60) stem = stem.slice(0, 60);
  // Force ext to the whitelist-derived value to neutralize spoofed extensions.
  return `${stem}.${fallbackExt || ext || "bin"}`;
}

function validateImageForUpload(file) {
  if (!file) return { ok: false, reason: "No file provided" };
  const ct = String(file.type || "").toLowerCase();
  const safeExt = IMAGE_MIME_WHITELIST[ct];
  if (!safeExt) {
    return { ok: false, reason: "Unsupported image type (allowed: JPG, PNG, WebP, GIF)" };
  }
  if (typeof file.size !== "number" || file.size < IMAGE_MIN_BYTES) {
    return { ok: false, reason: "Image is empty or too small" };
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return { ok: false, reason: "Image exceeds 5MB limit" };
  }
  return {
    ok: true,
    contentType: ct,
    safeName: sanitizeUploadFileName(file.name, safeExt),
  };
}

/**
 * LocationPicker — small MapLibre map with a single draggable marker the
 * business owner uses to drop a precise pin on the Guild map. Reuses the
 * keyless OSM raster style from PirateMap so it shares no API quota.
 */
function LocationPicker({
  latitude,
  longitude,
  island,
  onChange,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Resolve the initial pin position: explicit coords > island centroid > USVI
  // center. Computed inside the init effect so it doesn't drift on rerenders.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const startLat = Number.isFinite(latitude) ? latitude
      : (island && ISLAND_CENTROIDS[island] ? ISLAND_CENTROIDS[island].lat : USVI_BOUNDS.center[1]);
    const startLng = Number.isFinite(longitude) ? longitude
      : (island && ISLAND_CENTROIDS[island] ? ISLAND_CENTROIDS[island].lng : USVI_BOUNDS.center[0]);
    const hasPin = Number.isFinite(latitude) && Number.isFinite(longitude);

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: [
              "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
              "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
              "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
            ],
            tileSize: 256,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      center: [startLng, startLat],
      zoom: hasPin ? 15 : 12,
      minZoom: 8,
      maxZoom: 18,
      attributionControl: true,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    const marker = new maplibregl.Marker({ color: "#b88a3d", draggable: true })
      .setLngLat([startLng, startLat])
      .addTo(map);
    marker.on("dragend", () => {
      const { lat, lng } = marker.getLngLat();
      onChangeRef.current?.({ lat, lng });
    });

    mapRef.current = map;
    markerRef.current = marker;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep marker in sync if parent state changes (e.g. island swap recenters,
  // Clear pin button drops it back to centroid).
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const hasPin = Number.isFinite(latitude) && Number.isFinite(longitude);
    const target = hasPin
      ? { lat: latitude, lng: longitude }
      : (island && ISLAND_CENTROIDS[island])
        ? { lat: ISLAND_CENTROIDS[island].lat, lng: ISLAND_CENTROIDS[island].lng }
        : { lat: USVI_BOUNDS.center[1], lng: USVI_BOUNDS.center[0] };
    marker.setLngLat([target.lng, target.lat]);
    map.easeTo({ center: [target.lng, target.lat], zoom: hasPin ? 15 : 12, duration: 400 });
  }, [latitude, longitude, island]);

  const recenterOnIsland = () => {
    if (!island || !ISLAND_CENTROIDS[island]) return;
    const { lat, lng } = ISLAND_CENTROIDS[island];
    onChange?.({ lat, lng });
  };

  const clearPin = () => {
    onChange?.(null);
  };

  const hasPrecisePin = Number.isFinite(latitude) && Number.isFinite(longitude);

  return (
    <div className="location-picker">
      <div className="location-picker__map" ref={containerRef} />
      <div className="location-picker__controls">
        <div className="location-picker__coords">
          {hasPrecisePin ? (
            <>
              <strong>Pin:</strong> {latitude.toFixed(4)}, {longitude.toFixed(4)}
            </>
          ) : (
            <em>No precise pin set — using island fallback.</em>
          )}
        </div>
        <div className="location-picker__buttons">
          <button
            type="button"
            className="location-picker__btn"
            onClick={recenterOnIsland}
            disabled={!island}
            title={island ? "Recenter pin on this island" : "Pick an island first"}
          >
            <FontAwesomeIcon icon={faLocationCrosshairs} /> Recenter on island
          </button>
          {hasPrecisePin && (
            <button
              type="button"
              className="location-picker__btn location-picker__btn--ghost"
              onClick={clearPin}
              title="Drop the pin and use the island fallback"
            >
              <FontAwesomeIcon icon={faCircleXmark} /> Clear pin
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditBusinessListing;
